import { useRef, useState } from 'react';
import {
  OTHER_MISSING_ITEM_MAX_LENGTH,
  STANDARD_ROOM_ITEMS,
  isValidRoomNumber,
  type InspectionType,
} from '@room-verify/shared';
import { ApiError, api } from '../api';
import { ar, errorMessage } from '../ar';
import { IconAlert, IconCheckCircle } from '../components/icons';
import { Sheet } from '../components/overlays';
import { Button, Notice } from '../components/primitives';

export interface EditTarget {
  id: string;
  roomNumber: string;
  inspectionType: InspectionType;
  itemsComplete: boolean;
  missingItems: string[];
  otherMissingItem: string | null;
}

/**
 * Administrative correction of a completed report.
 *
 * Four fields, and that is the entire editable surface of the product. The dialog says
 * so explicitly, because a manager who expects to be able to fix a photo timestamp
 * should learn that here rather than by trying.
 */
export function EditDialog({
  open,
  token,
  target,
  onClose,
  onSaved,
  onAuthExpired,
}: {
  open: boolean;
  token: string;
  target: EditTarget | null;
  onClose: () => void;
  onSaved: (message: string) => void;
  onAuthExpired: () => void;
}) {
  const [roomNumber, setRoomNumber] = useState(target?.roomNumber ?? '');
  const [inspectionType, setInspectionType] = useState<InspectionType>(
    target?.inspectionType ?? 'CHECKOUT',
  );
  const [complete, setComplete] = useState(target?.itemsComplete ?? true);
  const [selected, setSelected] = useState<Set<string>>(new Set(target?.missingItems ?? []));
  const [otherText, setOtherText] = useState(target?.otherMissingItem ?? '');
  const [otherEnabled, setOtherEnabled] = useState(Boolean(target?.otherMissingItem));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);

  // Re-seed whenever a different report is opened.
  const seededFor = useRef<string | null>(null);
  if (target && seededFor.current !== target.id) {
    seededFor.current = target.id;
    setRoomNumber(target.roomNumber);
    setInspectionType(target.inspectionType);
    setComplete(target.itemsComplete);
    setSelected(new Set(target.missingItems));
    setOtherText(target.otherMissingItem ?? '');
    setOtherEnabled(Boolean(target.otherMissingItem));
    setError(null);
  }

  if (!target) return null;

  const otherValue = otherEnabled ? otherText.trim() : '';
  const roomValid = isValidRoomNumber(roomNumber.trim());
  const itemsValid = complete || selected.size > 0 || otherValue.length > 0;
  const canSave = roomValid && itemsValid;

  const toggleItem = (item: string) => {
    setError(null);
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(item)) next.delete(item);
      else next.add(item);
      return next;
    });
  };

  const save = async () => {
    if (inFlight.current) return;
    if (!roomValid) return setError(ar.errInvalidRoom);
    if (!itemsValid) return setError(ar.errMissingItemsEmpty);

    inFlight.current = true;
    setBusy(true);
    setError(null);
    try {
      const result = await api.editReport(token, target.id, {
        roomNumber: roomNumber.trim(),
        inspectionType,
        complete,
        missingItems: complete ? [] : [...selected],
        otherMissingItem: complete ? null : otherValue || null,
      });
      onSaved(result.unchanged ? ar.noChangesMade : ar.savedSuccessfully);
    } catch (caught) {
      // The dialog stays open on failure: closing it would discard the manager's edits
      // and leave them unsure whether the change landed.
      if (caught instanceof ApiError && caught.isAuthExpiry) onAuthExpired();
      else setError(errorMessage(caught instanceof ApiError ? caught.code : 'NETWORK'));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <Sheet
      open={open}
      title={ar.editReport}
      onClose={onClose}
      footer={
        <>
          <Button variant="accent" onClick={save} loading={busy} disabled={!canSave} block>
            {busy ? ar.saving : ar.saveChanges}
          </Button>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            {ar.cancel}
          </Button>
        </>
      }
    >
      <div className="edit-form">
        <Notice tone="info">{ar.editHint}</Notice>

        <div className="edit-row">
          <label className="field">
            <span className="field-label">{ar.roomNumber}</span>
            <input
              className="input num"
              value={roomNumber}
              onChange={(event) => {
                setRoomNumber(event.target.value.replace(/\D/g, '').slice(0, 4));
                setError(null);
              }}
              inputMode="numeric"
              dir="ltr"
              aria-invalid={!roomValid || undefined}
            />
          </label>

          <label className="field">
            <span className="field-label">{ar.inspectionType}</span>
            <select
              className="input"
              value={inspectionType}
              onChange={(event) => setInspectionType(event.target.value as InspectionType)}
            >
              <option value="CHECKOUT">{ar.checkout}</option>
              <option value="CLEAN_ROOM">{ar.clean}</option>
            </select>
          </label>
        </div>

        <fieldset className="edit-items">
          <legend className="field-label">{ar.roomItems}</legend>

          <div className="segmented" role="radiogroup" aria-label={ar.roomItems}>
            <button
              type="button"
              role="radio"
              aria-checked={complete}
              className={`segment${complete ? ' is-active' : ''}`}
              onClick={() => {
                setComplete(true);
                setError(null);
              }}
            >
              <IconCheckCircle size={14} />
              {ar.itemsCompleteOption}
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={!complete}
              className={`segment${!complete ? ' is-active' : ''}`}
              onClick={() => {
                setComplete(false);
                setError(null);
              }}
            >
              <IconAlert size={14} />
              {ar.itemsMissingOption}
            </button>
          </div>

          {!complete && (
            <div className="edit-items-body">
              <p className="field-label">{ar.selectMissingItems}</p>

              {/* Item names stay in English — the hotel's inventory identifiers,
                  matching exactly what the supervisor selected. */}
              <div className="item-grid">
                {STANDARD_ROOM_ITEMS.map((item) => {
                  const isSelected = selected.has(item);
                  return (
                    <label key={item} className={`item-chip${isSelected ? ' is-selected' : ''}`}>
                      <input type="checkbox" checked={isSelected} onChange={() => toggleItem(item)} />
                      <span className="item-check" aria-hidden="true" />
                      <span className="item-name" lang="en" dir="ltr">
                        {item}
                      </span>
                    </label>
                  );
                })}

                <label className={`item-chip${otherEnabled ? ' is-selected' : ''}`}>
                  <input
                    type="checkbox"
                    checked={otherEnabled}
                    onChange={() => {
                      setOtherEnabled((value) => !value);
                      setError(null);
                    }}
                  />
                  <span className="item-check" aria-hidden="true" />
                  <span className="item-name">{ar.otherItem}</span>
                </label>
              </div>

              {otherEnabled && (
                <label className="field">
                  <span className="sr-only">{ar.otherItemPlaceholder}</span>
                  <input
                    className="input"
                    value={otherText}
                    onChange={(event) => {
                      setOtherText(event.target.value.slice(0, OTHER_MISSING_ITEM_MAX_LENGTH));
                      setError(null);
                    }}
                    placeholder={ar.otherItemPlaceholder}
                    maxLength={OTHER_MISSING_ITEM_MAX_LENGTH}
                  />
                </label>
              )}
            </div>
          )}
        </fieldset>

        {error && (
          <Notice tone="critical" role="alert">
            {error}
          </Notice>
        )}
      </div>
    </Sheet>
  );
}
